import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { IoCloseSharp } from 'react-icons/io5';
import { appendNormalizerNodeToPipeline, editNormalizerNode } from '../../../api/api';
import { usePipeline } from '../../../context/PipelineContext';
import { validateNodeName } from '../../../name_validation';
import { notifyError } from '../../../errors/notifyError';
import { OP } from '../../../api/operations';
import './NormalizerConfigModal.css';

// Set the app element for accessibility
try {
  Modal.setAppElement('#root');
} catch (error) {
  console.warn('Could not set app element for Modal:', error);
  // This prevents the app from crashing if the root element isn't available
}

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '0.375rem',
    padding: '1.5rem',
    maxWidth: '500px',
    width: '90%',
    position: 'relative',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 9999, // Higher z-index to ensure it appears on top
  }
};

const NormalizerConfigModal = ({ isOpen, onClose, onConfigured, isReconfiguration = false, initialData = {} }) => {
  // Log when the component renders
  console.log("NormalizerConfigModal rendering with props:", { isOpen, isReconfiguration, initialData });
  
  // Helper function to determine the initial interval value
  const getInitialInterval = () => {
    if (!initialData.interval) return '15';
    
    // Log the interval for debugging
    console.log("Initial interval value:", initialData.interval);
    
    // More precise check for the interval value
    if (initialData.interval === '15 mins' || initialData.interval === 15 || initialData.interval === '15') {
      return '15';
    } else if (initialData.interval === '1 hr' || initialData.interval === 60 || initialData.interval === '60') {
      return '60';
    }
    
    // Fallback to checking if it includes '15' or '60'
    return initialData.interval.toString().includes('15') ? '15' : '60';
  };
  
  // Initialize state with values from initialData
  const [nodeName, setNodeName] = useState(initialData.name || '');
  const [nodeNameError, setNodeNameError] = useState('');
  const [interval, setInterval] = useState(getInitialInterval());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  
  // Determine if we're in edit mode (has a name AND isConfigured is true but not reconfiguring)
  const isEditing = !!initialData.name && initialData.isConfigured === true && !isReconfiguration;
  
  const { session } = usePipeline(); // Use the context to get the session
  
  // Log when the modal's open state changes
  useEffect(() => {
    console.log("NormalizerConfigModal isOpen changed:", { isOpen });
  }, [isOpen]);
  
  // Validate node name
  useEffect(() => {
    // Always validate the node name, even if it's empty
    const validation = validateNodeName(nodeName);
    setNodeNameError(validation.isValid ? '' : validation.errorMessage);
  }, [nodeName]);

  const handleConfigure = async () => {
    try {
      setSubmitError(null);
      // Validate node name using the validation function
      const nameValidation = validateNodeName(nodeName);
      if (!nameValidation.isValid) {
        setNodeNameError(nameValidation.errorMessage);
        // No need for alert as the button will be disabled
        return;
      }
      
      // Convert interval to the format expected by the node
      const intervalLabel = interval === '15' ? '15 mins' : '1 hr';
      
      // Convert interval to the target value expected by the API (as a number)
      const target = parseInt(interval, 10);
      
      console.log("Configuring with interval:", {
        intervalValue: interval,
        intervalLabel,
        targetValue: target
      });
      
      // Get the input node name and position from initialData
      const inputNodeName = initialData.inputNodeName;
      const position = initialData.position || { x: 0, y: 0 };
      
      // Set submitting state to show loading indicator
      setIsSubmitting(true);
      
      // Determine if we're editing an existing node or creating a new one
      // We're editing if isEditing is true (which means the node has a name AND is already configured)
      const isEditingExistingNode = isEditing;
      
      console.log("Configuration mode:", {
        isEditingExistingNode,
        isEditing,
        isReconfiguration,
        name: initialData.name,
        isConfigured: initialData.isConfigured
      });
      
      // Make the API call if we have a valid pipeline name
      if (session.pipeline_name !== "Unsaved_Pipeline") {
        try {
          if (isEditingExistingNode) {
            // We're editing an existing node
            console.log(`Calling API to edit normalizer node: ${initialData.name} in pipeline: ${session.pipeline_name}`);
            console.log(`New target: ${target}`);
            
            // In edit mode, we only update the target interval, not the name or input node
            await editNormalizerNode(
              session.pipeline_name,
              initialData.name,  // Original name
              initialData.name,  // Keep the same name in edit mode
              target,
              null               // Don't change the input node in edit mode
            );
            
            console.log(`Successfully edited normalizer node ${initialData.name}`);
          } else if (!isReconfiguration && inputNodeName) {
            // We're creating a new node
            console.log(`Calling API to append normalizer node: ${nodeName} to pipeline: ${session.pipeline_name}`);
            console.log(`Input node: ${inputNodeName}, Target: ${target}, Position: (${position.x}, ${position.y})`);
            
            await appendNormalizerNodeToPipeline(
              session.pipeline_name,
              nodeName,
              target,
              inputNodeName,
              position.x,
              position.y
            );
            
            console.log(`Successfully added normalizer node ${nodeName} to backend`);
          } else if (isReconfiguration) {
            console.log(`Skipping API call for node ${nodeName} as it's being reconfigured by parent component`);
          }
        } catch (apiError) {
          console.error('Error calling normalizer node API:', apiError);
          const info = notifyError(apiError, {
            operation: isEditingExistingNode ? OP.node.editNormalizer : OP.node.appendNormalizer,
            showToast: false,
          });
          setSubmitError(info);
          setIsSubmitting(false);
          return; // Exit early on API error
        }
      } else {
        console.log(`Skipping API call for unsaved pipeline`);
      }
      
      // Prepare the data to return to the parent component
      const configuredData = {
        name: isEditing ? initialData.name : nodeName, // Keep original name in edit mode
        interval: intervalLabel
      };
      
      console.log("Returning configured data:", configuredData);
      
      // Call the callback to update the UI
      onConfigured(configuredData);
      
      // Reset submitting state
      setIsSubmitting(false);
      
      // Note: We don't close the modal here as it will be closed by the parent component
    } catch (error) {
      console.error('Error configuring normalizer node:', error);
      const info = notifyError(error, { showToast: false });
      setSubmitError(info);
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={customStyles}
      contentLabel="Normalizer Configuration"
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      ariaHideApp={false} // Prevents warning about appElement
    >
      <div className="normalizer-modal">
        <button onClick={onClose} className="normalizer-modal-close">
          <IoCloseSharp size={24} />
        </button>
        
        <h2 className="normalizer-modal-title">
          {isReconfiguration ? "Reconfigure Normalizer Node" : 
           isEditing ? "Edit Normalizer Parameters" : 
           "Configure New Normalizer Node"}
        </h2>

        {submitError && (
          <div
            role="alert"
            className="normalizer-modal-error"
            style={{
              marginTop: '0.75rem',
              marginBottom: '0.75rem',
              padding: '0.75rem',
              borderRadius: '0.375rem',
              background: 'rgba(239, 68, 68, 0.12)',
              border: '1px solid rgba(239, 68, 68, 0.35)',
              color: '#b91c1c',
              fontSize: '0.95rem',
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>{submitError.title}</div>
            <div>{submitError.message}</div>
          </div>
        )}
        
        <form onSubmit={(e) => { e.preventDefault(); handleConfigure(); }}>
          <div className="normalizer-modal-field">
            <label className="normalizer-modal-label" htmlFor="node-name-input">
              Node Name: <span className="required">*</span>
            </label>
            {isEditing ? (
              <input
                id="node-name-input"
                type="text"
                value={nodeName}
                className="normalizer-modal-input"
                disabled={true}
                title="Node name cannot be changed in edit mode"
                readOnly
              />
            ) : (
              <input
                id="node-name-input"
                type="text"
                value={nodeName}
                onChange={(e) => setNodeName(e.target.value)}
                onBlur={() => {
                  // Validate again on blur to ensure we show errors when user leaves the field
                  const validation = validateNodeName(nodeName);
                  setNodeNameError(validation.isValid ? '' : validation.errorMessage);
                }}
                className={`normalizer-modal-input ${nodeNameError ? 'error' : ''}`}
                placeholder="Enter node name (letters, digits, hyphens, underscores, periods)"
                aria-invalid={!!nodeNameError}
                aria-describedby={nodeNameError ? "node-name-error" : undefined}
              />
            )}
            {!isEditing && nodeNameError && (
              <div className="normalizer-modal-error" id="node-name-error" role="alert">{nodeNameError}</div>
            )}
            {!isEditing && !nodeNameError && (
              <div className="normalizer-modal-hint">
                Valid characters: letters, digits, hyphens (-), underscores (_), periods (.)
              </div>
            )}
          </div>
          
          <div className="normalizer-modal-field">
            <label className="normalizer-modal-label" htmlFor="interval-select">
              Interval:
            </label>
            <select 
              id="interval-select"
              value={interval} 
              onChange={(e) => setInterval(e.target.value)} 
              className="normalizer-modal-select"
            >
              <option value="15">15 mins</option>
              <option value="60">1hr</option>
            </select>
          </div>
          
          <div className="normalizer-modal-button-container">
            <button 
              type="submit"
              className="normalizer-modal-configure-button"
              disabled={
                isSubmitting || 
                (!isEditing && (!!nodeNameError || !nodeName))
              }
              title={
                nodeNameError ? nodeNameError : 
                (!nodeName && !isEditing) ? 'Node name is required' : ''
              }
            >
              {isSubmitting ? 'Processing...' : 
                isReconfiguration ? 'Reconfigure' : 
                isEditing ? 'Save Changes' : 
                'Configure'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default NormalizerConfigModal;